/**
 * GxSheet: the one bottom sheet behind every overlay on a phone (popovers, menus). A titled, modal dialog that slides up from the foot:
 * focus goes to the title on open and back to the invoker on close, Escape or a tap on the scrim closes it, Tab stays inside, the page
 * behind does not scroll, and opening one closes any other. `auto` grows with its content; `full` fills the screen below the notch.
 * Dialog and Drawer are sheets by CSS alone at the same widths; they share `useScrollLock`.
 */
import { useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./sheet.css";

let locks = 0;
let savedY = 0;

/** Locks page scroll while mounted (`html[data-scroll-locked]`; the CSS applies it only where overlays are sheets). Counted, so nested overlays unlock once. */
export function useScrollLock(): void {
  useEffect(() => {
    if (locks === 0) { savedY = window.scrollY; document.documentElement.dataset.scrollLocked = ""; }
    locks += 1;
    return () => {
      locks -= 1;
      if (locks > 0) return;
      delete document.documentElement.dataset.scrollLocked;
      if (window.scrollY !== savedY) window.scrollTo(0, savedY);
    };
  }, []);
}

/** The sheet on screen now: a new one asks it to close first. */
let current: { readonly close: () => void } | null = null;

const FOCUSABLE = "a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex='-1'])";

export function GxSheet({ title, onClose, variant = "auto", footer, invoker, id, testId, children }: {
  readonly title: string; readonly onClose: () => void; readonly children: ReactNode; readonly footer?: ReactNode;
  /** `auto` (default): as tall as its content up to 90% of the screen. `full`: the whole screen below the notch. */
  readonly variant?: "auto" | "full";
  /** Where focus goes on close; default the element focused when it opened. */
  readonly invoker?: HTMLElement | null;
  readonly id?: string; readonly testId?: string;
}) {
  const titleId = useId();
  const panel = useRef<HTMLElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  useScrollLock();
  useEffect(() => {
    const me = { close: () => close.current() };
    current?.close();
    current = me;
    const active = document.activeElement;
    const back = invoker ?? (active instanceof HTMLElement && active !== document.body ? active : null);
    heading.current?.focus({ preventScroll: true });
    const key = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented) { e.stopPropagation(); close.current(); } };
    document.addEventListener("keydown", key);
    return () => {
      document.removeEventListener("keydown", key);
      if (current === me) current = null;
      back?.focus({ preventScroll: true });
    };
    // The invoker is read once, at open.
  }, []);
  const trap = (e: KeyboardEvent) => {
    if (e.key !== "Tab" || panel.current === null) return;
    const items = [...panel.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
    const first = items[0]; const last = items[items.length - 1];
    if (first === undefined || last === undefined) { e.preventDefault(); heading.current?.focus(); return; }
    const at = document.activeElement;
    if (e.shiftKey && (at === first || at === heading.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
  };
  return createPortal(
    <div className="gx-scrim gx-bsheet-wrap" onClick={() => close.current()}>
      <section ref={panel} id={id} className="gx-bsheet" role="dialog" aria-modal="true" aria-labelledby={titleId} data-variant={variant} data-presentation="sheet" data-testid={testId}
        onKeyDown={trap} onClick={(e) => e.stopPropagation()}>
        <div className="gx-bsheet-handle" aria-hidden="true" />
        <header className="gx-bsheet-h">
          <h2 id={titleId} ref={heading} tabIndex={-1} className="gx-h2">{title}</h2>
          <button type="button" className="gx-x" aria-label={`Close ${title}`} onClick={() => close.current()}>×</button>
        </header>
        <div className="gx-bsheet-b">{children}</div>
        {footer === undefined ? null : <footer className="gx-bsheet-f">{footer}</footer>}
      </section>
    </div>,
    document.body,
  );
}
